import { useAppDispatch, useAppSelector } from 'hooks/redux';
import type { List } from 'immutable';
// ROOT
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Grid, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
// MUI
import { makeStyles } from 'tss-react/mui';

import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { categoriesSelectors } from 'app-components/flux/categories';
import { actionHelpers } from 'app-components/flux/core';
import { dateSelectors } from 'app-components/flux/date';
import { freeToSpendSelectors } from 'app-components/flux/free-to-spend';
import { freeToSpendTypes } from 'app-components/flux/free-to-spend';
import type { FreeToSpend } from 'app-components/flux/free-to-spend/freeToSpendTypes';
// APP-COMPONENTS
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
} from 'app-components/flux/scheduled-transactions';
import { transactionsActions } from 'app-components/flux/transactions';
import type { transactionsTypes } from 'app-components/flux/transactions';
import type { TransactionItem, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { tracker } from 'app-components/utils/core';
import paperAirplaneCrash from 'assets/illustrations/Plane-Crash.png';
// ASSETS
import SadStar from 'assets/illustrations/Sad-Star-New.svg';
import GreenWallet from 'assets/images/spendingPlan/GreenWallet.svg';
import MoneyBag from 'assets/images/spendingPlan/Money_Bag.svg';
import SuccessCheck from 'assets/images/spendingPlan/SuccessCheck.svg';
import BillScoutLoadingView from 'components/Dialogs/BillScout/BillScoutLoadingView';
import { showAddScheduledTransactionDialog } from 'components/Dialogs/ScheduledTransactionAddDialog/actions';
// COMPONENTS
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import Dump from 'components/Dump';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';
import AmountField, { ShowSignEnum, ShowColorEnum } from 'components/QuickenControls/AmountField';
import { showTxnDetailsDialog } from 'components/Transactions/DetailsDialog/actions';
import ZeroStateView from 'components/ZeroStateView';
import SimpleContentView from '../SimpleContentView';

const useStyles = makeStyles()((theme) => ({
  incomeSeriesRow: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    minWidth: '100%',
    minHeight: 60,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    alignItems: 'center',
    opacity: 0.5,
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
    '&.opacity': {
      opacity: 1,
    },
  },
  simpleContent: {
    marginTop: 'auto',
    marginBottom: 'auto',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  txnImg: {
    width: 62,
    height: 62,
  },
  headerRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  gridContainer: {
    minWidth: '100%',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  textAlignRight: {
    textAlign: 'right',
  },
  rightAlign: {
    justifyContent: 'flex-end',
    display: 'flex',
    flexDirection: 'row',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
  },
  payee: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  rowToggle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginLeft: -12, // ignore padding of check icon
    marginBottom: theme.spacing(1),
  },
  tooltip: {
    marginTop: -4, // Fix tooltip causing broken spacing
    marginLeft: 4,
  },
  amountRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: theme.spacing(2),
  },
  bold: {
    fontWeight: 500,
  },
  skipPadding: {
    marginRight: theme.spacing(2),
  },
}));

export const incomeIndexEnums = Object.freeze({
  PRE_RECURRING: 'PRE_RECURRING',
  ADD_RECURRING: 'ADD_RECURRING',
  PRE_NON_RECURRING: 'PRE_NON_RECURRING',
  ADD_NON_RECURRING: 'ADD_NON_RECURRING',
  SUCCESS: 'SUCCESS',
});

export type IncomeIndex = (typeof incomeIndexEnums)[keyof typeof incomeIndexEnums];

const typeEnums = scheduledTransactionsTypes.STTypeEnum;
const { GSFStageEnum } = freeToSpendTypes;

function IncomeSeriesRow({
  key,
  series,
  seriesIdsToChange,
  setSeriesIdsToChange,
}: {
  key: string;
  series: any;
  seriesIdsToChange: string[];
  setSeriesIdsToChange: (items: string[]) => void;
}) {
  const { classes, cx } = useStyles();

  const categories = useAppSelector((state) => categoriesSelectors.getCategoriesById(state));

  const editSeries = useCallback(
    (series: scheduledTransactionsTypes.ScheduledTransactionType) => {
      const seriesId = series?.id ?? '';
      if (seriesIdsToChange.includes(seriesId)) {
        setSeriesIdsToChange(seriesIdsToChange.filter((myId) => myId !== seriesId));
      } else {
        setSeriesIdsToChange([...seriesIdsToChange, seriesId]);
      }
    },
    [seriesIdsToChange, setSeriesIdsToChange],
  );

  const isChangedToInclude =
    series?.excludeF2S === 'NONE'
      ? !seriesIdsToChange.includes(series?.id ?? '')
      : seriesIdsToChange.includes(series?.id ?? '');

  const frequency = series && customFrequencyUtils.recurrenceRowAlias(series.recurrence);

  return (
    <Grid
      key={key}
      id={key}
      container
      className={cx(classes.incomeSeriesRow, isChangedToInclude && 'opacity')}
      onClick={() => editSeries(series)}
    >
      <Dump obj={series} />
      <Grid item xl={3} lg={3} md={3} sm={3} xs={3} className={classes.row}>
        <Checkbox checked={isChangedToInclude} />
        <Typography className={classes.payee} variant='paragraphRegular'>
          {series?.transaction?.payee}
        </Typography>
      </Grid>
      <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
        <Typography variant='paragraphRegular'>
          {categories.get(series?.transaction?.coa?.id ?? '')?.name ?? 'Uncategorized'}
        </Typography>
      </Grid>
      <Grid item xl={2} lg={2} md={2} sm={2} xs={2}>
        <Typography variant='paragraphRegular'>{frequency}</Typography>
      </Grid>
      <Grid item xl={2} lg={2} md={2} sm={2} xs={2} className={classes.rightAlign}>
        <AmountField
          showColor={ShowColorEnum.POSITIVE_ONLY}
          showSign={ShowSignEnum.ALWAYS}
          variant='paragraphRegular'
          value={series?.transaction?.amount}
          className={classes.textAlignRight}
        />
      </Grid>
    </Grid>
  );
}

function IncomeTxnRow({
  key,
  txnItem,
  txnIdsToChange,
  setTxnIdsToChange,
  nonRecurringTxns,
}: {
  key: string;
  txnItem: TransactionItem;
  txnIdsToChange: string[];
  setTxnIdsToChange: (idList: string[]) => void;
  nonRecurringTxns: List<TransactionRecord>;
}) {
  const { classes, cx } = useStyles();

  const categories = useAppSelector((state) => categoriesSelectors.getCategoriesById(state));

  const { item } = txnItem;
  const transaction = nonRecurringTxns.find((myTxn) => myTxn.id === txnItem.transaction.id);

  const isChangedToInclude =
    transaction?.isExcludedFromF2S === false
      ? !txnIdsToChange.includes(transaction?.id ?? '')
      : txnIdsToChange.includes(transaction?.id ?? '');

  const editTxns = useCallback(
    (txn: transactionsTypes.TransactionProps | undefined) => {
      const txnId = txn?.id || '';
      if (txnIdsToChange.includes(txnId)) {
        setTxnIdsToChange(txnIdsToChange.filter((myId) => myId !== txnId));
      } else {
        setTxnIdsToChange([...txnIdsToChange, txnId]);
      }
    },
    [setTxnIdsToChange, txnIdsToChange],
  );

  return (
    <Grid
      key={key}
      id={key}
      container
      className={cx(classes.incomeSeriesRow, isChangedToInclude && 'opacity')}
      onClick={() => editTxns(transaction)}
    >
      <Dump obj={txnItem} />
      <Grid item xl={3} lg={3} md={3} sm={3} xs={3} className={classes.row}>
        <Checkbox checked={isChangedToInclude} />
        <Typography className={classes.payee} variant='paragraphRegular'>
          {item?.payee}
        </Typography>
      </Grid>
      <Grid item xl={3} lg={3} md={3} sm={3} xs={3}>
        <Typography variant='paragraphRegular'>
          {categories?.get(item?.coa?.id ?? '')?.name ?? 'Uncategorized'}
        </Typography>
      </Grid>
      <Grid item xl={2} lg={2} md={2} sm={2} xs={2}>
        <Typography variant='paragraphRegular'>Once</Typography>
      </Grid>
      <Grid item xl={2} lg={2} md={2} sm={2} xs={2}>
        <AmountField
          showColor={ShowColorEnum.POSITIVE_ONLY}
          showSign={ShowSignEnum.ALWAYS}
          variant='paragraphRegular'
          value={item?.amount}
          className={classes.textAlignRight}
        />
      </Grid>
    </Grid>
  );
}

function AutoInclusionToggle({
  callback,
}: {
  callback?: (isChecked: boolean) => void;
}) {
  const { classes } = useStyles();
  const [excludeIncomeByDefault, setExcludeIncomeByDefault] = usePreferenceV2('sSPExcludeIncomeByDefault');

  const toggleCheckbox = () => {
    if (callback) {
      callback(!excludeIncomeByDefault);
    }
    setExcludeIncomeByDefault(!excludeIncomeByDefault);
  };

  return (
    <div className={classes.rowToggle}>
      <Checkbox checked={!excludeIncomeByDefault} onClick={toggleCheckbox} />
      <Typography variant={'paragraphRegular'}>
        Automatically include all of my non-recurring income each month.
      </Typography>
      <InfoTooltipIcon
        className={classes.tooltip}
        tooltipPlacement='top'
        tooltipText='Select this option to automatically include any new or non-recurring income to your Spending Plan each month without needing to review it.'
      />
    </div>
  );
}

function TxnContent({
  recurring,
  renderRows,
  callback,
}: {
  recurring: boolean;
  renderRows: React.ReactNode;
  callback?: (isChecked: boolean) => void;
}) {
  const { classes } = useStyles();

  const mainText = recurring ? (
    <Typography variant='bodyRegular'>
      We found the following&nbsp;
      <Typography component='span' variant='bodyMedium'>
        recurring income
      </Typography>
      . Uncheck any you don’t want to include in your Spending Plan. You can always add any income later.
    </Typography>
  ) : (
    <Typography variant='bodyRegular'>
      We found the following&nbsp;
      <Typography component='span' variant='bodyMedium'>
        non-recurring income
      </Typography>
      . Check any transactions you’d like to include in your Spending Plan. You can always add any income later.
    </Typography>
  );
  const image = recurring ? MoneyBag : GreenWallet;

  return (
    <>
      <div className={classes.headerRow}>
        {mainText}
        <img src={image} className={classes.txnImg} />
      </div>
      {recurring ? null : <AutoInclusionToggle callback={callback} />}
      <div className={classes.gridContainer}>{renderRows}</div>
    </>
  );
}

const sumTxnIncludedAmounts = (
  idsToInvert: string[],
  txns: List<TransactionItem>,
  nonRecurringTxns: List<TransactionRecord>,
) => {
  return txns.reduce((sum: number, txnItem: TransactionItem) => {
    const { item } = txnItem;
    const transaction = nonRecurringTxns.find((myTxn) => myTxn?.id === txnItem?.transaction?.id);

    const isInverted = idsToInvert.includes(transaction?.id ?? '');

    // If inverted, flip inclusion
    const exclude = isInverted ? !transaction?.isExcludedFromF2S : transaction?.isExcludedFromF2S;

    return exclude ? sum : sum + (item?.amount || 0);
  }, 0);
};

const sumSeriesIncludedAmounts = (
  idsToInvert: string[],
  series: List<scheduledTransactionsTypes.ScheduledTransactionType>,
) => {
  return series.reduce((sum: number, item: scheduledTransactionsTypes.ScheduledTransactionType) => {
    const isInverted = idsToInvert.includes(item?.id ?? '');

    // Original inclusion logic
    const originallyIncluded = item?.excludeF2S === 'NONE';

    // If inverted, flip inclusion
    const include = isInverted ? !originallyIncluded : originallyIncluded;

    return include ? sum + (item?.transaction?.amount || 0) : sum;
  }, 0);
};

function Income() {
  const { classes, cx } = useStyles();
  const isMounted = useRef<boolean>();
  const dispatch = useAppDispatch();

  // ================= SELECTORS ==================
  const institutionLoginsIdsNotRefreshing = useAppSelector(institutionLoginsSelectors.getIdsRefreshing)?.isEmpty();
  const currentDate = useAppSelector(dateSelectors.getCurrentDateString);
  const freeToSpend: FreeToSpend = useAppSelector((state) =>
    freeToSpendSelectors.getFreeToSpendForDate(state, currentDate ?? ''),
  );
  const { income } = useAppSelector(scheduledTransactionsSelectors.getBillsAndIncome);
  const incomeTxnF2SData = useAppSelector(
    (state) => freeToSpendSelectors.getSpendingPlanBreakdownDataWithSplitsV2(state, freeToSpend).income,
  );

  // ================ PREFS ======================
  const [excludeIncomeByDefault] = usePreferenceV2('sSPExcludeIncomeByDefault');
  const [_gettingStartedFlowPhase, setGettingStartedFlowPhase] = usePreferenceV2(
    'sSPGettingStartedFlowPhase',
    GSFStageEnum.COMPLETE,
  );

  // All individual income txns not part of a recurring model! Concat included/excluded, filter recurring models, reduce to only txn then turn into array!
  const combinedNonRecurringTxnItems = useMemo(
    () => incomeTxnF2SData.excludeData.concat(incomeTxnF2SData.includeData).filter((txn) => !txn.transaction.stModelId),
    [incomeTxnF2SData],
  );
  const nonRecurringTxns = useMemo(
    () => combinedNonRecurringTxnItems.map((txn) => txn.transaction),
    [combinedNonRecurringTxnItems],
  );

  // =================== STATES =======================
  const [screenIndex, setScreenIndex] = useState<IncomeIndex>(incomeIndexEnums.PRE_RECURRING);
  const [forceLoading, setForceLoading] = useState(false);
  const [forceError, setForceError] = useState(false);

  const [seriesIdsToChange, setSeriesIdsToChange] = useState<string[]>([]);
  const [txnIdsToChange, setTxnIdsToChange] = useState<string[]>([]);

  // ========== TIMER REF ============
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  //  ================= VIEW STATE LOGIC =====================
  //  Views
  const showAccountsRefreshingView =
    !institutionLoginsIdsNotRefreshing &&
    (screenIndex === incomeIndexEnums.ADD_RECURRING || screenIndex === incomeIndexEnums.ADD_NON_RECURRING);
  const scheduledView = useLoadState(scheduledTransactionsSelectors);
  const noRecurringIncome = !income?.size;
  const noIncomeTxns = !nonRecurringTxns?.size;

  const mainView = useMemo(() => {
    const showSkeletonView = Boolean(loadState.skeletonStates.includes(scheduledView));

    // For some reason, the tryAgainStates must be separated into a separate const in order to prevent a lint error.
    const tryAgainStates = Boolean(loadState.tryAgainStates.includes(scheduledView));
    const showTryAgainView = Boolean(tryAgainStates || loadState.updateRequiredStates.includes(scheduledView));

    const showNoDataView = Boolean(loadState.noDataStates.includes(scheduledView));
    const showReadyView = Boolean(loadState.readyStates.includes(scheduledView));

    let myMainView = '';
    const loadingScreenOverride = showAccountsRefreshingView || forceLoading;
    if ((showReadyView || showNoDataView) && !loadingScreenOverride && !forceError) {
      myMainView = 'READY';
    } else if ((showTryAgainView && !loadingScreenOverride) || forceError) {
      myMainView = 'FAILED';
    } else if ((showSkeletonView || loadingScreenOverride) && !forceError) {
      myMainView = 'LOADING';
    }

    return myMainView;
  }, [forceError, forceLoading, scheduledView, showAccountsRefreshingView]);

  // =============== USE EFFECT VALUES =====================
  useEffect(() => {
    isMounted.current = true;
    tracker.track(tracker.events.startSPIncomeVerification);
    return () => {
      isMounted.current = false;
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []); // eslint-disable react-hooks/exhaustive-deps

  // ====================== ACTION HANDLING =======================

  const onClose = useCallback(
    (forceContinue?: boolean) => {
      if (forceContinue || screenIndex === incomeIndexEnums.SUCCESS) {
        tracker.track(tracker.events.completeSPIncomeVerification);
        setGettingStartedFlowPhase(GSFStageEnum.BILLS);
      }
      dispatch(rootUiActions.removeDialog());
    },
    [dispatch, screenIndex, setGettingStartedFlowPhase],
  );

  const setTimerForClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      onClose(true);
    }, 3000);
  }, [onClose]);

  const updateSeries = useCallback(() => {
    setForceLoading(true);
    const seriesUpdatePackage = income
      .map((mySeries) => {
        if (seriesIdsToChange.includes(mySeries?.id ?? '')) {
          const updatedExcludeF2S = mySeries.excludeF2S === 'NONE' ? 'SRC' : 'NONE';
          return mySeries
            .set('excludeF2S', updatedExcludeF2S)
            .setIn(['transaction', 'isExcludedFromF2S'], !mySeries?.transaction?.isExcludedFromF2S);
        }
        return mySeries;
      })
      .toList()
      .toArray();
    actionHelpers
      .bindPromiseAction(
        dispatch,
        scheduledTransactionsActions.batchScheduledTransactions,
      )(seriesUpdatePackage)
      .then((_resp: any) => {
        if (isMounted.current === true) {
          setForceLoading(false);
          setScreenIndex(incomeIndexEnums.PRE_NON_RECURRING);
        }
      })
      .catch((_error: any) => {
        setForceLoading(false);
        setForceError(true);
      });
  }, [dispatch, income, seriesIdsToChange]);

  const updateTxns = useCallback(() => {
    setForceLoading(true);
    const txnUpdatePackage = nonRecurringTxns
      .map((myTxn) => {
        if (txnIdsToChange.includes(myTxn?.id ?? '')) {
          return myTxn.set('isExcludedFromF2S', !myTxn.isExcludedFromF2S);
        }
        return myTxn;
      })
      .toArray();

    if (txnUpdatePackage.some((txn) => !txn.isExcludedFromF2S)) {
      tracker.track(tracker.events.verifiedOneTimeIncome);
    }

    actionHelpers
      .bindPromiseAction(
        dispatch,
        transactionsActions.batchTransactions,
      )(txnUpdatePackage as transactionsTypes.TransactionRecord[])
      .then((_resp: any) => {
        if (isMounted.current === true) {
          setForceLoading(false);
          setScreenIndex(incomeIndexEnums.SUCCESS);
          setTimerForClose();
        }
      })
      .catch((_error: any) => {
        setForceLoading(false);
        setForceError(true);
      });
  }, [setTimerForClose, dispatch, nonRecurringTxns, txnIdsToChange]);

  const onContinue = useCallback(() => {
    if (screenIndex === incomeIndexEnums.PRE_RECURRING) {
      setScreenIndex(incomeIndexEnums.ADD_RECURRING);
    }
    if (screenIndex === incomeIndexEnums.ADD_RECURRING) {
      if (noRecurringIncome) {
        setScreenIndex(incomeIndexEnums.PRE_NON_RECURRING);
      } else {
        updateSeries();
      }
    }
    if (screenIndex === incomeIndexEnums.PRE_NON_RECURRING) {
      setScreenIndex(incomeIndexEnums.ADD_NON_RECURRING);
    }
    if (screenIndex === incomeIndexEnums.ADD_NON_RECURRING) {
      tracker.track(tracker.events.automaticallyIncludeIncome, {
        preference: !excludeIncomeByDefault,
      });
      if (noIncomeTxns) {
        setScreenIndex(incomeIndexEnums.SUCCESS);
        setTimerForClose();
      } else {
        updateTxns();
      }
    }
    if (screenIndex === incomeIndexEnums.SUCCESS) {
      onClose();
    }
  }, [
    excludeIncomeByDefault,
    noIncomeTxns,
    noRecurringIncome,
    onClose,
    screenIndex,
    setTimerForClose,
    updateSeries,
    updateTxns,
  ]);

  const addAccountFlow = useCallback(() => {
    dispatch(
      accountDiscoveryActions.doAccountDiscovery({
        initiator: 'spending plan GSF',
        mode: 'ADD-FI',
      }),
    );
  }, [dispatch]);

  const primaryButtonPressed = useCallback(() => {
    if (
      (noRecurringIncome && screenIndex === incomeIndexEnums.ADD_RECURRING) ||
      (noIncomeTxns && screenIndex === incomeIndexEnums.ADD_NON_RECURRING)
    ) {
      addAccountFlow();
    } else {
      onContinue();
    }
  }, [addAccountFlow, noIncomeTxns, noRecurringIncome, onContinue, screenIndex]);

  const addIncome = useCallback(() => {
    if (screenIndex === incomeIndexEnums.ADD_RECURRING) {
      dispatch(showAddScheduledTransactionDialog({ dialogType: typeEnums.INCOME }));
    } else {
      dispatch(showTxnDetailsDialog({ wrapperId: 'spending-plan-gsf' }));
    }
  }, [dispatch, screenIndex]);

  const changeAllItems = useCallback(
    (isChecked: boolean) => {
      const newTxnIds: string[] = [];

      combinedNonRecurringTxnItems.forEach((txnItem) => {
        const txnId = txnItem.transaction.id;
        const currentlyExcluded = txnItem.transaction.isExcludedFromF2S;

        // If we're checking all, include only those currently excluded
        if (!isChecked && currentlyExcluded && txnId) {
          newTxnIds.push(txnId);
        }
      });
      if (!isChecked) {
        setTxnIdsToChange(newTxnIds);
      }
    },
    [combinedNonRecurringTxnItems, setTxnIdsToChange],
  );

  // ====================== RENDERING =======================

  const disableContinue = screenIndex === incomeIndexEnums.SUCCESS || forceLoading;
  let continueText = '';
  if (screenIndex === incomeIndexEnums.PRE_RECURRING) continueText = 'Continue';
  if (screenIndex === incomeIndexEnums.ADD_RECURRING && !noRecurringIncome) continueText = 'Add income';
  if (screenIndex === incomeIndexEnums.ADD_RECURRING && noRecurringIncome) continueText = 'Connect accounts';
  if (screenIndex === incomeIndexEnums.PRE_NON_RECURRING) continueText = 'Continue';
  if (screenIndex === incomeIndexEnums.ADD_NON_RECURRING && !noIncomeTxns) continueText = 'Add income';
  if (screenIndex === incomeIndexEnums.ADD_NON_RECURRING && noIncomeTxns) continueText = 'Connect accounts';
  if (screenIndex === incomeIndexEnums.SUCCESS) continueText = '';

  let tertiaryText = '';
  if (screenIndex === incomeIndexEnums.PRE_RECURRING) tertiaryText = '';
  if (screenIndex === incomeIndexEnums.ADD_RECURRING && !noRecurringIncome) tertiaryText = 'Add missing income';
  if (screenIndex === incomeIndexEnums.PRE_NON_RECURRING) tertiaryText = '';
  if (screenIndex === incomeIndexEnums.ADD_NON_RECURRING && !noIncomeTxns) tertiaryText = 'Add missing income';
  if (screenIndex === incomeIndexEnums.SUCCESS) tertiaryText = '';

  let dialogTitle = '';
  if (screenIndex === incomeIndexEnums.PRE_RECURRING) dialogTitle = '';
  if (screenIndex === incomeIndexEnums.ADD_RECURRING) dialogTitle = 'Add income to Spending Plan';
  if (screenIndex === incomeIndexEnums.PRE_NON_RECURRING) dialogTitle = '';
  if (screenIndex === incomeIndexEnums.ADD_NON_RECURRING) dialogTitle = 'Add income to Spending Plan';
  if (screenIndex === incomeIndexEnums.SUCCESS) dialogTitle = '';

  const renderIncomeSeriesRows = income.map((series, index) => (
    <IncomeSeriesRow
      key={`series-${index}`}
      series={series}
      seriesIdsToChange={seriesIdsToChange}
      setSeriesIdsToChange={setSeriesIdsToChange}
    />
  ));
  const renderIncomeTxnRows = combinedNonRecurringTxnItems.map((txnItem, index) => (
    <IncomeTxnRow
      key={`txn-${index}`}
      setTxnIdsToChange={setTxnIdsToChange}
      txnIdsToChange={txnIdsToChange}
      nonRecurringTxns={nonRecurringTxns}
      txnItem={txnItem}
    />
  ));

  const renderScreen = useMemo(() => {
    if (mainView === 'LOADING') {
      return (
        <div className={classes.simpleContent}>
          {showAccountsRefreshingView ? <BillScoutLoadingView loading={true} /> : <LoadingView size={100} />}
        </div>
      );
    }
    if (mainView === 'FAILED') {
      return (
        <div className={classes.simpleContent}>
          <ZeroStateView
            primaryVariant='displayRegular'
            secondaryVariant='bodyRegular'
            id='gsf-failure'
            primary='Data has failed to load'
            secondary={'Data has failed to load, please refresh and try again'}
            icon={paperAirplaneCrash}
          />
        </div>
      );
    }
    if (mainView === 'READY') {
      if (screenIndex === incomeIndexEnums.PRE_RECURRING) {
        return (
          <SimpleContentView
            image={<img width='128px' height='128px' src={MoneyBag} />}
            mainText={'Verify income to include'}
            secondaryText={
              'Your Spending Plan starts with your income—let’s verify the transactions you want to include each month.'
            }
          />
        );
      }
      if (screenIndex === incomeIndexEnums.ADD_RECURRING) {
        if (noRecurringIncome) {
          return (
            <SimpleContentView
              mainVariant={'headlineRegular'}
              image={<img width='168px' height='168px' src={SadStar} />}
              mainText={'No income found!'}
              secondaryText={
                'Connect any accounts that receive income in \n' + 'to Simplifi or manually add your income.'
              }
            />
          );
        }
        return <TxnContent recurring={true} renderRows={renderIncomeSeriesRows} />;
      }
      if (screenIndex === incomeIndexEnums.PRE_NON_RECURRING) {
        return (
          <SimpleContentView
            image={<img width='128px' height='128px' src={SuccessCheck} />}
            mainText={'Nice!'}
            secondaryText={'Next, we’ll add any non-recurring income.'}
          />
        );
      }
      if (screenIndex === incomeIndexEnums.ADD_NON_RECURRING) {
        if (noIncomeTxns) {
          return (
            <SimpleContentView
              image={<img width='168px' height='168px' src={SadStar} />}
              mainVariant={'headlineRegular'}
              mainText={'No income found!'}
              secondaryText={
                'Connect any accounts that receive income in \n' + 'to Simplifi or manually add your income.'
              }
            />
          );
        }
        return <TxnContent recurring={false} renderRows={renderIncomeTxnRows} callback={changeAllItems} />;
      }
      if (screenIndex === incomeIndexEnums.SUCCESS) {
        return (
          <SimpleContentView
            image={<img width='128px' height='128px' src={SuccessCheck} />}
            mainText={'Sweet!'}
            secondaryText={'We’ve added your income to your Spending Plan!'}
          />
        );
      }
    }
    return null;
  }, [
    mainView,
    classes.simpleContent,
    showAccountsRefreshingView,
    screenIndex,
    noRecurringIncome,
    renderIncomeSeriesRows,
    noIncomeTxns,
    renderIncomeTxnRows,
    changeAllItems,
  ]);

  const zeroStateButtons = useMemo(
    () => (
      <>
        <QButton variant='text' onClick={onContinue} className={classes.skipPadding}>
          Skip for now
        </QButton>
        <QButton variant='outlined' onClick={addIncome}>
          Manually Add Income
        </QButton>
      </>
    ),
    [classes.skipPadding, addIncome, onContinue],
  );

  const amountRow = useCallback(
    (amount: number) => (
      <>
        <Typography variant='paragraphRegular'>Total this month:&nbsp;</Typography>
        <AmountField
          showColor={ShowColorEnum.POSITIVE_ONLY}
          showSign={ShowSignEnum.ALWAYS}
          variant='paragraphMedium'
          value={amount}
          className={cx(classes.textAlignRight, classes.bold)}
        />
      </>
    ),
    [classes.bold, classes.textAlignRight, cx],
  );

  const secondaryNode = useMemo(() => {
    if (
      (noRecurringIncome && screenIndex === incomeIndexEnums.ADD_RECURRING) ||
      (noIncomeTxns && screenIndex === incomeIndexEnums.ADD_NON_RECURRING)
    ) {
      return <div className={classes.amountRow}>{zeroStateButtons}</div>;
    }
    if (!noRecurringIncome && screenIndex === incomeIndexEnums.ADD_RECURRING) {
      return (
        <div className={classes.amountRow}>
          {amountRow(sumSeriesIncludedAmounts(seriesIdsToChange, income.toList()))}
        </div>
      );
    }
    if (!noIncomeTxns && screenIndex === incomeIndexEnums.ADD_NON_RECURRING) {
      return (
        <div className={classes.amountRow}>
          {amountRow(sumTxnIncludedAmounts(txnIdsToChange, combinedNonRecurringTxnItems, nonRecurringTxns))}
        </div>
      );
    }
    return null;
  }, [
    amountRow,
    classes.amountRow,
    combinedNonRecurringTxnItems,
    income,
    noIncomeTxns,
    noRecurringIncome,
    nonRecurringTxns,
    screenIndex,
    seriesIdsToChange,
    txnIdsToChange,
    zeroStateButtons,
  ]);

  return (
    <StdDialog
      onClose={() => onClose()}
      open={true}
      sharedcomponentid={'FIRST_TIME_USE_DIALOG_V2'}
      showCloseButton={screenIndex !== incomeIndexEnums.SUCCESS}
      size='md'
      title={dialogTitle}
      PaperProps={{
        sx: { overflowY: 'hidden' },
      }}
    >
      <StdDialogContent
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {renderScreen}
      </StdDialogContent>
      {screenIndex !== incomeIndexEnums.SUCCESS && mainView === 'READY' && (
        <StdDialogActions
          primary={{
            id: 'primary-button',
            disabled: disableContinue,
            children: continueText,
            onClick: primaryButtonPressed,
            variant: 'contained',
          }}
          secondaryNode={secondaryNode}
          tertiaryAction={{
            id: 'tertiary-button',
            children: tertiaryText,
            variant: 'text',
            onClick: addIncome,
          }}
        />
      )}
    </StdDialog>
  );
}

export default Income;
